/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, HandHeart, Leaf } from 'lucide-react';
import PostureBadge from '@/components/sustainability/PostureBadge';
import SignalGrid from '@/components/sustainability/SignalGrid';
import RecoveryIntegrityCard from '@/components/sustainability/RecoveryIntegrityCard';

/**
 * Counselor Sustainability — admin / terra_head team rollup.
 *
 * Sorted by composite ascending: lowest first = "support priority", framed
 * as mentorship cue, never as ranking. Cards are calm, breathing, paginated
 * vertically with generous whitespace.
 */
export default function CounselorSustainabilityPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sustainability/team?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="counselor-sustainability-page" className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239] flex items-center gap-1.5">
              <Leaf className="w-3 h-3" /> Sustainability
            </p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-2">
              Recovery & Sustainability Signals™
            </h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-2 max-w-2xl leading-relaxed">
              How are the people who hold the ecosystem? Sorted by support priority —
              never ranking. The team member at the top is a calm signal that
              mentorship, recovery, or workload re-balancing may help.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 14, 30].map(d => (
              <button key={d} data-testid={`team-days-${d}`} onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>{d}d</button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing…
          </div>
        ) : !data || data.members.length === 0 ? (
          <div data-testid="counselor-sustainability-empty"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No counselors in scope yet.
          </div>
        ) : (
          <>
            <p className="text-[11px] text-[#8A8681] italic mb-5 max-w-2xl">{data.framing_note}</p>
            <ul className="space-y-4">
              {data.members.map(m => <MemberCard key={m.user_id} m={m} />)}
            </ul>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function MemberCard({ m }) {
  return (
    <li data-testid={`team-member-${m.user_id}`}
        className="rounded-2xl border border-[#EBE8E3] bg-white p-6">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base text-[#2C2A29] truncate font-['Work_Sans']">{m.name}</p>
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mt-0.5">{m.role}</p>
        </div>
        <PostureBadge posture={m.posture} />
      </header>
      <div className="mt-4">
        <SignalGrid signals={m.signals} raw={m.raw_inputs} />
      </div>
      {m.recovery_integrity && (
        <div className="mt-3">
          <RecoveryIntegrityCard integrity={m.recovery_integrity} compact />
        </div>
      )}
      {m.posture.code !== "STEWARDED" && (
        <footer className="mt-4 pt-3 border-t border-[#F4F2EE] flex items-center gap-2">
          <HandHeart className="w-3 h-3 text-[#8C6239]" />
          <p className="text-[11px] text-[#8C6239] italic">
            Mentorship support, lighter days, or a peer reflection conversation may help here.
          </p>
        </footer>
      )}
    </li>
  );
}
